import { DataType, Form, FormOptions, Section } from '@globaleris/ui.library'
import { Box, makeStyles, useTheme } from '@material-ui/core'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../../../api'
import {
  Concept,
  ConceptLayoutConfigurationModel,
  NewConceptPrice
} from '../../../../api/Argentia/endpoints/concepts/model/concepts'
import { EnumPriceCalculationType, EnumRedondeo } from '../../../../api/Argentia/enums'
import { EnumPrecioUnitario } from '../../../../api/oData/enums/enumPrecioUnitario'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { PriceExpressionValidator } from '../../../../components/shared/priceExpressionValidator'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../../../helpers'
import { extractPriceIdsFromExpression } from '../../../../helpers/extractPriceIdsFromExpression'
import { startDateIsBeforeOrSameEndDate } from '../../../../helpers/formValidationsHelper'
import { getCustomerDomain } from '../../../../redux'
import { ConceptModificationType } from '../../../products/enums/conceptModificationType.enum'
import { buildDiscriminantFormInputs, hasAnyDiscriminantField } from '../../concepts.helpers'
import { mapPriceValuesToFormFields } from '../../concepts.mapper'
import { ConceptModifiedFormEditModel } from '../../concepts.models'
import {
  getBaseConceptByID,
  getConceptPriceValues,
  getConfigurationLayout,
  getPriceByID
} from '../../concepts.service'
import { ConceptPriceCard } from '../../edit/components/conceptPriceCard'
import { conceptModifiedFormStyles } from './conceptModified.edit.styles'

interface ConceptModifiedFormProps {
  isEditMode?: boolean
  formData: ConceptModifiedFormEditModel
  onFormChange: (formState: ConceptModifiedFormEditModel, formIsCompleted: boolean) => void
  conceptModificationType?: ConceptModificationType
  // PriceExpressionValidator props
  showPriceValidator?: boolean
  commodity: EnumCommodity
  activeExpression?: string
  activeExpressionIsValid?: boolean
  activeRounding?: any
  onSelectValidExpression: (expression: string | undefined) => void
  onSelectPriceCalculationType: (priceCalculationType: EnumPriceCalculationType) => void
  activePriceCalculationType?: EnumPriceCalculationType | null
  onIsValid: (isValid: boolean) => void
}

interface ConceptModifiedFormValidation {
  startDate: boolean
  allComplete: boolean
}

const initialFormValidation: ConceptModifiedFormValidation = {
  startDate: false,
  allComplete: false
}

export const ConceptModifiedEditForm: React.FC<ConceptModifiedFormProps> = ({
  isEditMode,
  formData: propsFormData,
  onFormChange,
  conceptModificationType,
  // PriceExpressionValidator props
  showPriceValidator = false,
  commodity,
  activeExpressionIsValid,
  activeRounding,
  onSelectValidExpression,
  onSelectPriceCalculationType,
  activePriceCalculationType,
  onIsValid
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'conceptsModified', 'conceptsEdit'])
  // Import Styles
  const currentTheme = useTheme()
  const useStyles = makeStyles(conceptModifiedFormStyles(currentTheme))
  const classes = useStyles()

  const [formData, setFormData] = useState<ConceptModifiedFormEditModel>(
    propsFormData || ({} as ConceptModifiedFormEditModel)
  )

  // Sync local state with props
  useEffect(() => {
    if (propsFormData) {
      setFormData(propsFormData)
    }
  }, [propsFormData])

  const [formValidations, setFormValidations] = useState<ConceptModifiedFormValidation>({
    ...initialFormValidation,
    startDate: !!formData?.startDate,
    allComplete: Object.keys(formData).length > 0
  })

  const [invalidDates, setInvalidDates] = useState<boolean>(false)
  const [showPriceEdit, setShowPriceEdit] = useState<boolean>(!!formData?.idPrice)
  const [showFormula, setShowFormula] = useState<boolean>(
    formData?.priceType === EnumPriceType.Formula
  )
  const [isLoadingPrices, setIsLoadingPrices] = useState<boolean>(false)
  const [isFetchingBaseConcept, setIsFetchingBaseConcept] = useState<boolean>(false)
  const [baseConceptPrices, setBaseConceptPrices] = useState<NewConceptPrice[] | null>(null)
  const [baseConceptConfiguration, setBaseConceptConfiguration] =
    useState<ConceptLayoutConfigurationModel | null>(null)

  const handleFormIsEnabled = useCallback(
    (key: keyof ConceptModifiedFormValidation, value: boolean): boolean => {
      let formIsCompleted = false

      setFormValidations(prevState => {
        formIsCompleted = Object.keys(initialFormValidation)
          .filter(k => k !== 'allComplete')
          .every(k => (k === key ? value : prevState[k as keyof ConceptModifiedFormValidation]))

        return {
          ...prevState,
          [key]: value,
          allComplete: formIsCompleted
        }
      })

      return formIsCompleted
    },
    []
  )

  const validateDates = useCallback((startDate: string, endDate: string): void => {
    if (startDate && endDate) {
      const isInvalid = !moment(startDate).isSameOrBefore(moment(endDate))
      setInvalidDates(isInvalid)
    } else {
      setInvalidDates(false)
    }
  }, [])

  const fetchBaseConcept = async (conceptId: number) => {
    setIsFetchingBaseConcept(true)
    try {
      const response = await getBaseConceptByID(conceptId)
      if (response.success && response.data) {
        const concept = response.data as Concept
        setBaseConceptPrices((concept.conceptPrice as NewConceptPrice[]) ?? null)

        // Fetch the real configuration layout based on calculationType
        if (concept.calculationType) {
          const configResponse = await getConfigurationLayout(concept.calculationType)
          if (configResponse.success && configResponse.data) {
            setBaseConceptConfiguration(configResponse.data as ConceptLayoutConfigurationModel)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching base concept:', error)
    } finally {
      setIsFetchingBaseConcept(false)
    }
  }

  // Load prices and replace {{id}} placeholders in expression
  useEffect(() => {
    if (formData?.expression && commodity) {
      // Extract all price IDs from expression
      const priceIds = extractPriceIdsFromExpression(formData.expression)

      if (priceIds.length > 0) {
        setIsLoadingPrices(true)

        // Fetch all prices
        Promise.all(priceIds.map(priceId => getPriceByID(priceId)))
          .then(responses => {
            let updatedExpression = formData.expression!

            // Replace each {{id}} with its corresponding price code
            responses.forEach((response, index) => {
              if (response.success && response.data && response.data.code) {
                const priceId = priceIds[index]
                // Replace the first occurrence of {{priceId}} with the price code
                updatedExpression = updatedExpression.replace(
                  new RegExp(`\\{\\{${priceId}\\}\\}`),
                  response.data.code
                )
              }
            })

            if (updatedExpression !== formData.expression) {
              const updatedFormData = { ...formData, expression: updatedExpression }
              setFormData(updatedFormData)
              onFormChange(updatedFormData, formValidations.allComplete)
            }
            setIsLoadingPrices(false)
          })
          .catch(() => {
            setIsLoadingPrices(false)
          })
      }
    }
  }, []) // Run only on mount

  // Load price values (parametro / periods) when opening the form with a non-Formula price
  useEffect(() => {
    if (!propsFormData?.idPrice) return

    // Se necesita la configuración del concepto tanto para Fórmula (tarjeta de referencia)
    // como para Parametro/ParametroPeriodo (campos discriminantes: región impuestos, etc.)
    fetchBaseConcept(propsFormData.masterConceptId)

    if (propsFormData?.priceType === EnumPriceType.Formula) {
      return
    }

    if (propsFormData?.priceType !== EnumPriceType.Formula) {
      setIsLoadingPrices(true)
      getConceptPriceValues(propsFormData.idPrice)
        .then(response => {
          if (response.success && response.data) {
            const valueFields = mapPriceValuesToFormFields(response.data, propsFormData.priceType)
            const updatedFormData = { ...propsFormData, ...valueFields }
            setFormData(updatedFormData)
            onFormChange(updatedFormData, formValidations.allComplete)
          }
        })
        .finally(() => {
          setIsLoadingPrices(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsFormData?.idPrice, propsFormData?.priceType]) // Run when the form opens with a price to load

  const normalizedFormData = useMemo(
    () => ({
      ...formData,
      hasPrice: formData?.hasPrice
    }),
    [formData]
  )

  const options: FormOptions = useMemo(
    () => ({
      elevation: 0,
      columns: 2,
      analytics: {
        customer: getCustomerDomain(),
        view: t('conceptsModified:forms.edit.analytics')
      }
    }),
    []
  )

  const sections: Section[] = useMemo(
    () => [
      ...(conceptModificationType !== ConceptModificationType.PRODUCT
        ? [
            {
              title: t('conceptsModified:forms.edit.sections.reference'),
              inputs: [
                {
                  name: 'id',
                  type: DataType.NUMBER,
                  display: false
                },
                {
                  name: 'productContractStartDate',
                  label: t('conceptsModified:forms.edit.inputs.productContractStartDate'),
                  type: DataType.DATE,
                  readOnly: true
                },
                {
                  name: 'productContractEndDate',
                  label: t('conceptsModified:forms.edit.inputs.productContractEndDate'),
                  type: DataType.DATE,
                  readOnly: true
                }
              ]
            }
          ]
        : []),
      {
        title: t('conceptsModified:forms.edit.sections.editable'),
        inputs: [
          {
            name: 'startDate',
            label: t('conceptsModified:forms.edit.inputs.startDate'),
            type: DataType.DATE,
            inputSize: 6,
            required: true,
            minDate: normalizedFormData?.productContractStartDate
              ? moment(normalizedFormData.productContractStartDate).format('YYYY-MM-DD')
              : undefined,
            maxDate: normalizedFormData?.productContractEndDate
              ? moment(normalizedFormData.productContractEndDate).format('YYYY-MM-DD')
              : undefined,
            errorState: invalidDates,
            errorText: t('conceptsModified:forms.edit.errorText.invalidStartDate'),
            onChange: (value, formState) => {
              const isComplete = !!value
              const formIsCompleted = handleFormIsEnabled('startDate', isComplete)
              const castFormState = formState as ConceptModifiedFormEditModel

              // Calculate date validity directly without depending on state
              const startDateValue = value ? value.toString() : undefined
              const endDateValue = castFormState?.endDate
                ? castFormState.endDate.toString()
                : undefined
              const datesAreInvalid = !startDateIsBeforeOrSameEndDate(startDateValue, endDateValue)

              // Update visual error state
              validateDates(startDateValue || '', endDateValue || '')

              // Use calculated validity, not state
              onFormChange(castFormState, formIsCompleted && !datesAreInvalid)
            }
          },
          {
            name: 'endDate',
            label: t('conceptsModified:forms.edit.inputs.endDate'),
            type: DataType.DATE,
            inputSize: 6,
            clearable: true,
            minDate: normalizedFormData?.productContractStartDate
              ? moment(normalizedFormData.productContractStartDate).format('YYYY-MM-DD')
              : undefined,
            maxDate: normalizedFormData?.productContractEndDate
              ? moment(normalizedFormData.productContractEndDate).format('YYYY-MM-DD')
              : undefined,
            errorState: invalidDates,
            errorText: t('conceptsModified:forms.edit.errorText.invalidEndDate'),
            onChange: (value, formState) => {
              const castFormState = formState as ConceptModifiedFormEditModel

              // Calculate date validity directly without depending on state
              const startDateValue = castFormState?.startDate
                ? castFormState.startDate.toString()
                : undefined
              const endDateValue = value ? value.toString() : undefined
              const datesAreInvalid = !startDateIsBeforeOrSameEndDate(startDateValue, endDateValue)

              // Update visual error state
              validateDates(startDateValue || '', endDateValue || '')

              // endDate is optional
              const formIsCompleted = formValidations.allComplete

              // Use calculated validity, not state
              onFormChange(castFormState, formIsCompleted && !datesAreInvalid)
            }
          },
          {
            name: 'customDescription',
            label: t('conceptsModified:forms.edit.inputs.customDescription'),
            type: DataType.TEXT,
            inputSize: 12,
            required: false,
            display: conceptModificationType !== ConceptModificationType.PRODUCT,
            onChange: (value, formState) => {
              const castFormState = formState as ConceptModifiedFormEditModel

              // Calculate date validity directly without depending on state
              const startDateValue = castFormState?.startDate
                ? castFormState.startDate.toString()
                : undefined
              const endDateValue = castFormState?.endDate
                ? castFormState.endDate.toString()
                : undefined
              const datesAreInvalid = !startDateIsBeforeOrSameEndDate(startDateValue, endDateValue)

              const formIsCompleted = formValidations.allComplete

              onFormChange(castFormState, formIsCompleted && !datesAreInvalid)
            }
          },
          {
            name: 'hasPrice',
            label: t('conceptsModified:forms.add.inputs.priceEdit'),
            type: DataType.SWITCH,
            display: conceptModificationType !== ConceptModificationType.PRODUCT,
            onChange: (value, formState) => {
              const isPriceEditEnabled = !!value
              const castFormState = formState as ConceptModifiedFormEditModel
              setShowPriceEdit(isPriceEditEnabled)
              setFormData(castFormState)
              onFormChange(castFormState, formValidations.allComplete)
            }
          },
          {
            name: 'priceType',
            label: t('conceptsModified:forms.add.inputs.priceType'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumPriceType,
              'enums:EnumPriceTypes',
              [EnumPriceType.Parametro, EnumPriceType.ParametroPeriodo, EnumPriceType.Formula]
            ),
            required: showPriceEdit,
            display: showPriceEdit,
            isLoading: isLoadingPrices,
            readOnly: conceptModificationType === ConceptModificationType.PRODUCT,
            onChange: (value, formState) => {
              const castFormState = formState as ConceptModifiedFormEditModel
              const isFormulaType = value === EnumPriceType.Formula
              setShowFormula(isFormulaType)

              if (isFormulaType) {
                fetchBaseConcept(formData.masterConceptId)
              }
              onFormChange(castFormState, false)
            }
          },
          {
            name: 'unitPrice',
            label: t('conceptsModified:forms.add.inputs.unitPrice'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
            required: showPriceEdit,
            display: showPriceEdit,
            isLoading: isLoadingPrices,
            onChange: (value, formState) => {
              const castFormState = formState as ConceptModifiedFormEditModel

              const formIsCompleted = formValidations.allComplete
              onFormChange(castFormState, formIsCompleted)
            }
          },
          {
            name: 'rounding',
            label: t('conceptsModified:forms.add.inputs.rounding'),
            type: DataType.SELECT,
            isLoading: isLoadingPrices,
            display: formData?.priceType !== EnumPriceType.Formula && showPriceEdit,
            selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
            required: formData?.priceType !== EnumPriceType.Formula,
            onChange: (value, formState) => {
              // Update formData to trigger re-render with new decimal scale
              const currentState = formState as ConceptModifiedFormEditModel
              setFormData({ ...currentState, rounding: value as EnumRedondeo })
            }
          }
        ]
      },
      // Discriminantes de precio (región impuestos, peaje, etc.) según layout del concepto.
      // Se muestran solos cuando es Fórmula (los valores van en la sección de Fórmula más abajo);
      // cuando no es Fórmula, se fusionan con la sección de Valores para no duplicar el título.
      ...(showPriceEdit &&
      baseConceptConfiguration &&
      conceptModificationType === ConceptModificationType.PRODUCT &&
      showFormula &&
      !isLoadingPrices &&
      hasAnyDiscriminantField(baseConceptConfiguration)
        ? [
            {
              title: t('conceptsModified:forms.add.valuesTitle'),
              inputs: buildDiscriminantFormInputs(
                baseConceptConfiguration,
                t,
                (field, value, formState) => {
                  const castFormState = {
                    ...(formState as ConceptModifiedFormEditModel),
                    [field]: value
                  }
                  setFormData(castFormState)
                  onFormChange(castFormState, formValidations.allComplete)
                }
              )
            }
          ]
        : []),
      // Only show values section when not using Formula type
      ...(!showFormula && showPriceEdit && !isLoadingPrices
        ? [
            {
              title: t('conceptsModified:forms.add.valuesTitle'),
              inputs: [
                // Discriminantes de precio (región impuestos, peaje, etc.) según layout del concepto
                ...(baseConceptConfiguration &&
                conceptModificationType === ConceptModificationType.PRODUCT
                  ? buildDiscriminantFormInputs(
                      baseConceptConfiguration,
                      t,
                      (field, value, formState) => {
                        const castFormState = {
                          ...(formState as ConceptModifiedFormEditModel),
                          [field]: value
                        }
                        setFormData(castFormState)
                        onFormChange(castFormState, formValidations.allComplete)
                      }
                    )
                  : []),
                {
                  name: 'value',
                  label: t('conceptsModified:forms.add.inputs.value'),
                  type: DataType.NUMBER,
                  display: formData?.priceType === EnumPriceType.Parametro,
                  required: showPriceEdit,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const castFormState = formState as ConceptModifiedFormEditModel
                    setFormData(castFormState)
                    onFormChange(castFormState, formValidations.allComplete)
                  }
                },
                {
                  name: 'period1Value',
                  label: t('conceptsModified:forms.add.inputs.period1Value'),
                  type: DataType.NUMBER,
                  display: formData?.priceType === EnumPriceType.ParametroPeriodo,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const castFormState = formState as ConceptModifiedFormEditModel
                    setFormData(castFormState)
                    onFormChange(castFormState, formValidations.allComplete)
                  }
                },
                {
                  name: 'period2Value',
                  label: t('conceptsModified:forms.add.inputs.period2Value'),
                  type: DataType.NUMBER,
                  display: formData?.priceType === EnumPriceType.ParametroPeriodo,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const castFormState = formState as ConceptModifiedFormEditModel
                    setFormData(castFormState)
                    onFormChange(castFormState, formValidations.allComplete)
                  }
                },
                {
                  name: 'period3Value',
                  label: t('conceptsModified:forms.add.inputs.period3Value'),
                  type: DataType.NUMBER,
                  display: formData?.priceType === EnumPriceType.ParametroPeriodo,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const castFormState = formState as ConceptModifiedFormEditModel
                    setFormData(castFormState)
                    onFormChange(castFormState, formValidations.allComplete)
                  }
                },
                {
                  name: 'period4Value',
                  label: t('conceptsModified:forms.add.inputs.period4Value'),
                  type: DataType.NUMBER,
                  display: formData?.priceType === EnumPriceType.ParametroPeriodo,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const castFormState = formState as ConceptModifiedFormEditModel
                    setFormData(castFormState)
                    onFormChange(castFormState, formValidations.allComplete)
                  }
                },
                {
                  name: 'period5Value',
                  label: t('conceptsModified:forms.add.inputs.period5Value'),
                  type: DataType.NUMBER,
                  display: formData?.priceType === EnumPriceType.ParametroPeriodo,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const castFormState = formState as ConceptModifiedFormEditModel
                    setFormData(castFormState)
                    onFormChange(castFormState, formValidations.allComplete)
                  }
                },
                {
                  name: 'period6Value',
                  label: t('conceptsModified:forms.add.inputs.period6Value'),
                  type: DataType.NUMBER,
                  display: formData?.priceType === EnumPriceType.ParametroPeriodo,
                  customDecimalScale: formData?.rounding,
                  clearable: true,
                  onChange: (value, formState) => {
                    const castFormState = formState as ConceptModifiedFormEditModel
                    setFormData(castFormState)
                    onFormChange(castFormState, formValidations.allComplete)
                  }
                }
              ]
            }
          ]
        : []),
      ...(showFormula && showPriceEdit && !isLoadingPrices
        ? [
            {
              title: t('conceptsModified:forms.add.expressionTitle'),
              inputs: []
            }
          ]
        : [])
    ],
    [
      formData,
      normalizedFormData,
      invalidDates,
      formValidations,
      handleFormIsEnabled,
      validateDates,
      onFormChange,
      conceptModificationType,
      showPriceEdit,
      showFormula,
      isLoadingPrices,
      baseConceptConfiguration
    ]
  )

  return (
    <Box className={classes.dialogForm}>
      <Form sections={sections} data={normalizedFormData} options={options} />

      {(showPriceValidator || showFormula) &&
        commodity &&
        showPriceEdit &&
        !isLoadingPrices &&
        !isFetchingBaseConcept && (
          <Box>
            <PriceExpressionValidator
              show={true}
              priceType={EnumPriceType.Formula}
              commodity={commodity}
              activePriceUnit={formData.unitPrice}
              activePriceCalculationType={activePriceCalculationType}
              activeExpression={formData.expression ?? undefined}
              activeExpressionIsValid={activeExpressionIsValid}
              activeRounding={activeRounding}
              onSelectValidExpression={onSelectValidExpression}
              onSelectPriceCalculationType={onSelectPriceCalculationType}
              onSelectPriceUnit={value => {}} // Not used, hidden
              onSelectRounding={value => {}} // Not used, hidden
              onIsValid={onIsValid}
              onIsLoading={() => {}}
              showPriceUnit={false}
              showDates={false}
              showRounding={false}
              showVersion={false}
            />

            {/* Base Concept Price Reference - Read-only */}
            {showFormula && baseConceptConfiguration && (
              <ConceptPriceCard
                isFetching={isFetchingBaseConcept}
                conceptPrice={baseConceptPrices}
                conceptId={formData.masterConceptId}
                configuration={baseConceptConfiguration}
                readOnly={true}
                raised={false}
                commodity={commodity}
              />
            )}
          </Box>
        )}
    </Box>
  )
}
